/**
 * Stav signálu na zákazníckom splittri — pre farebný prstenec na mape.
 *
 * Slúži ako kontrola PO NÁVRHU, pred zváraním: zelený stĺpik je pripravený,
 * červený alebo biely treba ešte dorobiť. Nie je to meranie, ale výpočet z
 * optickej cesty — ten istý, aký appka ukazuje pri výbere vlákna, takže čísla
 * sedia s tým, čo technik uvidí v kazete.
 *
 * Meria sa na VÝSTUPE splittra, teda vstupný výkon mínus útlm delenia — to je
 * to, čo naozaj dorazí k zákazníkovi. Pri 1:8 je to o 10,5 dB menej než na
 * vstupe, takže podľa vstupu by sa dalo ľahko prehliadnuť, že to nevyjde.
 */
import { Pole, Cable, ProjectSettings } from '../types';
import { traceOpticalPath } from './opticalPathTracer';
import { getSplitterLoss } from './splitterCatalog';

export type SignalStav = 'ziadny' | 'nezapojene' | 'prerusene' | 'slaby' | 'dobry';

export interface SignalInfo {
  stav: SignalStav;
  /** výkon na výstupe splittra v dBm, ak sa dal spočítať */
  vykon: number | null;
  /** na vstupe splittra, pred delením */
  vykonNaVstupe: number | null;
  typSplittra: string | null;
  popis: string;
}

export const SIGNAL_FARBY: Record<SignalStav, string | null> = {
  ziadny: null,
  nezapojene: '#ffffff',
  prerusene: '#ef4444',
  slaby: '#f59e0b',
  dobry: '#22c55e',
};

export const SIGNAL_POPIS: Record<SignalStav, string> = {
  ziadny: 'bez zákazníckeho splittra',
  nezapojene: 'splitter nemá zapojený vstup',
  prerusene: 'cesta k OLT je prerušená',
  slaby: 'signál je slabý',
  dobry: 'pripravené na zváranie',
};

const PRAH_ZELENA = -15;
const PRAH_ORANZOVA = -25;

export const prahy = (settings?: ProjectSettings | null) => ({
  zelena: settings?.signal_green_dbm ?? PRAH_ZELENA,
  oranzova: settings?.signal_orange_dbm ?? PRAH_ORANZOVA,
});

/** Zákaznícke splittery stĺpa — aj z jeho virtuálneho dvojčaťa, kde reálne sedia. */
const zakaznickeSplittery = (pole: Pole, poles: Pole[]): any[] => {
  const zdroje = [pole, ...poles.filter((p) => p.parent_pole_id === pole.id && p.is_virtual)];
  return zdroje.flatMap((z) =>
    ((z.cassette?.splitters || []) as any[]).filter((sp) => sp?.is_customer_splitter === true));
};

/**
 * Stav signálu pre jeden stĺp. Keď je na ňom viac zákazníckych splitterov,
 * rozhoduje ten NAJHORŠÍ — technik má vidieť, že tam ešte niečo treba dorobiť,
 * nie sa uspokojiť s tým, že jeden z nich je v poriadku.
 */
export const signalNaStlpe = (
  pole: Pole,
  poles: Pole[],
  cables: Cable[],
  settings?: ProjectSettings | null,
): SignalInfo => {
  const splittery = zakaznickeSplittery(pole, poles);
  if (splittery.length === 0) {
    return { stav: 'ziadny', vykon: null, vykonNaVstupe: null, typSplittra: null,
             popis: SIGNAL_POPIS.ziadny };
  }

  const p = prahy(settings);
  const poradie: Record<SignalStav, number> = {
    prerusene: 0, nezapojene: 1, slaby: 2, dobry: 3, ziadny: 4,
  };
  let najhorsi: SignalInfo | null = null;

  for (const sp of splittery) {
    let info: SignalInfo;
    const kabel = sp.input_cable_id;
    const vlakno = sp.input_fiber;

    if (!kabel || !vlakno) {
      info = { stav: 'nezapojene', vykon: null, vykonNaVstupe: null,
               typSplittra: sp.type || null, popis: SIGNAL_POPIS.nezapojene };
    } else {
      let vstup: number | null = null;
      try {
        const r = traceOpticalPath(kabel, vlakno, poles, cables);
        if (r.found && r.isValid) vstup = r.finalPower;
      } catch {
        /* nedá sa dotrasovať */
      }
      if (vstup === null) {
        info = { stav: 'prerusene', vykon: null, vykonNaVstupe: null,
                 typSplittra: sp.type || null, popis: SIGNAL_POPIS.prerusene };
      } else {
        const utlm = getSplitterLoss(sp.type);
        const vystup = vstup - utlm;
        const stav: SignalStav = vystup >= p.zelena ? 'dobry'
          : vystup >= p.oranzova ? 'slaby' : 'prerusene';
        info = {
          stav,
          vykon: vystup,
          vykonNaVstupe: vstup,
          typSplittra: sp.type || null,
          popis: stav === 'prerusene' ? 'signál je príliš slabý' : SIGNAL_POPIS[stav],
        };
      }
    }
    if (!najhorsi || poradie[info.stav] < poradie[najhorsi.stav]) najhorsi = info;
  }

  return najhorsi!;
};
